/**
 * Agent Brains section 5.3's missing half: not the scenario classifier, which
 * `scenario.ts` already builds and fully tests, but the "and a route" that
 * charter clause names next to it. `apps/web/src/lib/agents.ts`'s `routing`
 * entry, verbatim: produces "A scenario class and a route, with the reason
 * attached." Handoff: "Receives from Conversations. Hands to Scheduling,
 * Follow-ups, or you."
 *
 * `routeScenario`/`routeConversation` already decide the CLASS. This file
 * decides the DESTINATION that class hands off to, and the reason for it, as
 * one pure function from an existing `ScenarioRoute` to a `RouteDecision`. It
 * does not re-derive anything `scenario.ts` already owns: no keyword
 * matching, no scenario table, no graduation class. Read the scenario, read
 * off the destination.
 *
 * THE ONE RULE THIS FILE IS LEAST ALLOWED TO GET WRONG (CLAUDE.md section 3,
 * "Class D scenario rows: complaints, fraud-suspect, unknown" is T3,
 * permanent, human-only, never agent-writable; the registry's own `never`
 * line: "Never routes a Class D case to an agent"). `complaint_warranty`,
 * `fraud_suspect` and `unknown` are the map's three class D rows
 * (`scenario.ts`'s `SCENARIO_MAP`), and every one of them resolves to
 * `"human"` below, by an exhaustive switch with no default case (a scenario
 * added to the `Scenario` union without a case here fails the TypeScript
 * build, not silently), and again by a runtime invariant that throws rather
 * than ships if the two ever disagree. Section 0: precision beats recall, so
 * that invariant is not a warning, it is a thrown error.
 *
 * `vendor_spam` needed a fourth value beyond the charter's own three named
 * destinations ("Scheduling, Follow-ups, or you"), logged as a deliberate,
 * narrow deviation in FINDINGS.md TA-184 rather than folded silently into one
 * of the three: `scenario.ts`'s own `SCENARIO_MAP` row for it already reads
 * "router: label and archive" with `generates: false`, so it is neither an
 * agent hand-off nor a human escalation, it is routing's own terminal
 * disposition. Forcing it into `"human"` would put spam on the owner's queue
 * for no reason; forcing it into `"follow_ups"` would enter a spam sender
 * into nurture cadence.
 *
 * NEVER CUSTOMER-FACING. This is the "Never tells a customer their score"
 * half of the registry's `never` line, structural, not just documented: a
 * `RouteDecision`'s `reason` is an internal audit string written for an
 * operator reading the event log, and it names the scenario, the class and
 * sometimes the map's own framework language back at whoever reads it. It
 * must never be threaded into anything that composes or renders
 * customer-facing text, the same boundary `contact memory` and `gold`
 * few-shots cross with a quarantine wrapper (B2) because THEY are data a
 * customer could have shaped; this is worse to leak, because it is the
 * shop's own internal read on the customer, not anything the customer wrote.
 */

import type { Scenario, ScenarioClass, ScenarioRoute } from "./scenario";

/**
 * The three destinations the charter names, plus routing's own terminal
 * disposition for a scenario nothing should ever act on. Not a fifth
 * "conversations" value: while Scheduling and Follow-ups both stay unbuilt or
 * partial, every non-archived, non-human scenario keeps being answered by
 * Conversations exactly as it is today, which is a fact about today's
 * architecture, not a route Routing hands anywhere. See FINDINGS.md TA-184.
 */
export type RouteDestination = "scheduling" | "follow_ups" | "human" | "archived";

export interface RouteDecision {
  readonly destination: RouteDestination;
  /** Human-readable, operator-facing only. Never customer-facing (see file
   * doc comment above). */
  readonly reason: string;
}

const FOLLOW_UP_SCENARIOS: readonly Scenario[] = [
  "repair_inquiry",
  "ready_made_purchase",
  "custom_engagement",
  "price_challenge",
  "educated_buyer_debate",
  "review_ask",
];

function followUpReason(scenario: Scenario): string {
  return (
    `${scenario} carries no booking signal and is not class D, so there is nothing for ` +
    "Scheduling or a human to do yet. Follow-ups owns the per-lead clock until one of " +
    "those appears or the thread goes quiet."
  );
}

/**
 * The exhaustive part. No default case: adding a fourteenth `Scenario`
 * without a line here is a compile error, not a silent fall-through.
 *
 * `repair_inquiry`, `ready_made_purchase`, `custom_engagement`,
 * `price_challenge`, `educated_buyer_debate` and `review_ask` all fall
 * through to the SAME `follow_ups` default, and that grouping is the one real
 * judgment call in this file (logged in full in FINDINGS.md TA-184, not
 * hidden in code): section 5.3 names no scenario-to-destination table the way
 * section 9 names a scenario-to-framework one, so nothing here is read off a
 * spec sentence the way `consult_confirm`'s mapping is. `consult_confirm` is
 * the one scenario whose own matched patterns (appointment, consult, booking,
 * reschedule, confirm) and lead framework (commitment-consistency) are
 * already, explicitly, about booking or confirming an appointment, which is
 * Scheduling's own charter verbatim ("Consults booked, confirmed, and
 * attended"). Every other commercial scenario is left with Conversations
 * today (Scheduling needs a calendar integration it does not have, and
 * Follow-ups needs a real send-worthy silence, not a guess made at
 * reply-time), so `follow_ups` is the conservative default: it does not
 * invent a scheduling readiness or a human-escalation need that nothing in
 * this codebase can currently justify.
 */
function classify(scenario: Scenario): RouteDecision {
  switch (scenario) {
    case "complaint_warranty":
      return {
        destination: "human",
        reason: "complaint_warranty is class D (CLAUDE.md section 3): a human handles every complaint, never an agent.",
      };
    case "fraud_suspect":
      return {
        destination: "human",
        reason: "fraud_suspect is class D: freeze and hand to a human, never accuse, never an agent.",
      };
    case "unknown":
      return {
        destination: "human",
        reason: "unknown is class D: an unrecognised message goes to a human rather than a confident guess.",
      };
    case "vendor_spam":
      return {
        destination: "archived",
        reason:
          "vendor_spam's own framework is \"label and archive\": no reply, no agent hand-off, no human review.",
      };
    case "consult_confirm":
      return {
        destination: "scheduling",
        reason:
          "consult_confirm is about booking, confirming or rescheduling an appointment, which is Scheduling's own charter.",
      };
    case "ghost_revival":
    case "post_purchase_care":
      // TA-315: section 9's two outbound rows, which nothing in this repo can
      // reach from a live message (`scenario.ts` has no rule that emits
      // either, so `routeScenario` cannot return one). These cases exist to
      // keep the switch exhaustive and to say what the destination WOULD be
      // once a play emits one: both are cadence work against a contact who is
      // not mid-conversation, which is Follow-ups' own charter, and neither
      // is a booking or a class D hand-off. Nothing about the other eleven
      // scenarios changes.
      return {
        destination: "follow_ups",
        reason:
          `${scenario} is one of section 9's outbound rows: Follow-ups owns the per-lead clock ` +
          "and the frequency caps it runs under. No inbound classifier emits it today.",
      };
    case "repair_inquiry":
    case "ready_made_purchase":
    case "custom_engagement":
    case "price_challenge":
    case "educated_buyer_debate":
    case "review_ask":
      return { destination: "follow_ups", reason: followUpReason(scenario) };
    default: {
      const exhaustive: never = scenario;
      throw new Error(`decideRouteDestination: unhandled scenario "${String(exhaustive)}"`);
    }
  }
}

/**
 * Maps an already-decided `ScenarioRoute` (from `routeScenario`/
 * `routeConversation`) to a destination and a reason. Never reclassifies the
 * message itself, and never called on raw text: the one and only input this
 * function reads is a route `scenario.ts` already produced.
 */
export function decideRouteDestination(route: ScenarioRoute): RouteDecision {
  const decision = classify(route.scenario);

  // Belt and suspenders on top of the exhaustive switch above (section 0,
  // "fail loudly"). The switch is correct today by inspection; this line is
  // what keeps it correct if a future edit to `classify` ever mistypes a
  // class D case, or if a caller builds a `ScenarioRoute` by hand (a test
  // fixture, a future importer) whose `graduationClass` does not match what
  // `SCENARIO_MAP` says for its own `scenario`. Either way, a class D route
  // resolving to anything but "human" throws here rather than shipping.
  assertClassDNeverAgent(route.graduationClass, decision);
  assertUnclassedNeverAgent(route.graduationClass, decision);

  return decision;
}

function assertClassDNeverAgent(graduationClass: ScenarioClass, decision: RouteDecision): void {
  if (graduationClass === "D" && decision.destination !== "human") {
    throw new Error(
      `routing invariant violated: a class D route resolved to "${decision.destination}", not "human"`,
    );
  }
}

/** The two destinations that hand work to an agent rather than ending it. */
const AGENT_DESTINATIONS: readonly RouteDestination[] = ["scheduling", "follow_ups"];

/**
 * TA-315. Section 9's class column has one cell that is not a class: vendor
 * spam reads "n/a", because the row generates nothing and so has no send to
 * graduate. A row with no graduation bar has nothing that could qualify it
 * for autonomous handling, so it may end at `archived` or at a person, and
 * never at an agent. The same shape as the class D invariant above and for
 * the same reason (section 0): it throws rather than warning, because the
 * only way this fires is that someone gave an unclassed row an agent
 * destination.
 */
function assertUnclassedNeverAgent(graduationClass: ScenarioClass, decision: RouteDecision): void {
  if (graduationClass === "n/a" && AGENT_DESTINATIONS.includes(decision.destination)) {
    throw new Error(
      `routing invariant violated: a route with no graduation class resolved to "${decision.destination}"`,
    );
  }
}
